import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function ChangePassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Feil",
        description: "Passordene matcher ikke",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Feil",
        description: "Passordet må være minst 6 tegn",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      toast({
        title: "Suksess",
        description: "Passordet ditt har blitt oppdatert",
      });
      
      navigate("/settings");
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast({
        title: "Feil",
        description: error.message || "Kunne ikke oppdatere passordet",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container min-h-[calc(100vh-4rem)] bg-gradient-to-b from-orange-50/50 to-white/50">
      <Button
        variant="ghost"
        onClick={() => navigate("/settings")}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Tilbake til innstillinger
      </Button>

      <Card className="max-w-md mx-auto">
        <CardHeader className="space-y-1">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-orange-100">
              <KeyRound className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Endre passord</CardTitle>
              <CardDescription>
                Velg et nytt, sikkert passord for kontoen din
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nytt passord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Skriv inn nytt passord"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekreft nytt passord</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Bekreft nytt passord"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={loading}
            >
              {loading ? "Oppdaterer..." : "Oppdater passord"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}